import { EntityState } from '@reduxjs/toolkit';
import { z } from 'zod';

export enum AgentChatAttachmentType {
  IMAGE = 'image',
}

export interface AgentChatThread {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  matter_id?: string;
  system: boolean;
  coverage_id?: string | null;
}

export interface AgentChatMessage {
  message_id: string;
  thread_id: string;
  order: number;
  message: MessageType;
  attachments: AgentChatAttachment[];
  created_at: string;
}

export interface AgentChatAttachment {
  id: string;
  thread_id: string;
  name: string;
  content_type: string;
  size: number;
  attachment: string; // URL to the attachment
  created_at: string;
}

// Request types
export interface CreateAgentChatThread {
  title: string;
  initial_task?: string | null;
  workspace_id?: string | null;
  coverage_id?: string | null;
}

export interface UpdateAgentChatThread {
  title: string;
}

export interface CreateAgentChatMessageRequest {
  message: MessageType;
  attachment_ids: string[];
}

export interface NewChatMessage {
  message: MessageType;
  attachments: AgentChatAttachment[];
}

export interface CreateAgentChatAttachment {
  name: string;
  file: File;
  size: number;
  content_type?: string;
}

// Response states using entity adapters
export type AgentChatMessagesState = EntityState<AgentChatMessage, string>;

// Common fields for all message types
const MessageCommonFields = {
  content: z.union([
    z.string(),
    z.array(z.union([z.string(), z.record(z.any())])),
  ]),
  additional_kwargs: z.record(z.any()).optional(),
  response_metadata: z.record(z.any()).optional(),
  name: z.union([z.string(), z.null()]).default(null),
  id: z.union([z.string(), z.null()]).default(null),
};

// Tool call schema used by AIMessage and AIMessageChunk
const ToolCallSchema = z
  .object({
    name: z.string(),
    args: z.record(z.any()),
    id: z.union([z.string(), z.null()]),
    type: z.literal('tool_call').optional(),
  })
  .describe('Represents a request to call a tool.');

// Invalid tool call schema
const InvalidToolCallSchema = z
  .object({
    name: z.union([z.string(), z.null()]),
    args: z.union([z.string(), z.null()]),
    id: z.union([z.string(), z.null()]),
    error: z.union([z.string(), z.null()]),
    type: z.literal('invalid_tool_call').optional(),
  })
  .describe('Allowance for errors made by LLM.');

// Usage metadata schema
const UsageMetadataSchema = z
  .object({
    input_tokens: z.number().int(),
    output_tokens: z.number().int(),
    total_tokens: z.number().int(),
    input_token_details: z
      .object({
        audio: z.number().int().optional(),
        cache_creation: z.number().int().optional(),
        cache_read: z.number().int().optional(),
      })
      .optional(),
    output_token_details: z
      .object({
        audio: z.number().int().optional(),
        reasoning: z.number().int().optional(),
      })
      .optional(),
  })
  .describe('Usage metadata for a message, such as token counts.');

// Tool call chunk schema
const ToolCallChunkSchema = z
  .object({
    name: z.union([z.string(), z.null()]),
    args: z.union([z.string(), z.null()]),
    id: z.union([z.string(), z.null()]),
    index: z.union([z.number().int(), z.null()]),
    type: z.literal('tool_call_chunk').optional(),
  })
  .describe('A chunk of a tool call (e.g., as part of a stream).');

// AI Message
export const AIMessage = z
  .object({
    ...MessageCommonFields,
    type: z.literal('ai').default('ai'),
    example: z.boolean().default(false),
    tool_calls: z.array(ToolCallSchema).default([]),
    invalid_tool_calls: z.array(InvalidToolCallSchema).default([]),
    usage_metadata: z.union([UsageMetadataSchema, z.null()]).default(null),
  })
  .catchall(z.any())
  .describe('Message from an AI.');

// Human Message
export const HumanMessage = z
  .object({
    ...MessageCommonFields,
    type: z.literal('human').default('human'),
    example: z.boolean().default(false),
  })
  .catchall(z.any())
  .describe('Message from a human.');

// Chat Message
export const ChatMessage = z
  .object({
    ...MessageCommonFields,
    type: z.literal('chat').default('chat'),
    role: z.string(),
  })
  .catchall(z.any())
  .describe('Message that can be assigned an arbitrary speaker (i.e. role).');

// System Message
export const SystemMessage = z
  .object({
    ...MessageCommonFields,
    type: z.literal('system').default('system'),
  })
  .catchall(z.any())
  .describe('Message for priming AI behavior.');

// Function Message
export const FunctionMessage = z
  .object({
    ...MessageCommonFields,
    type: z.literal('function').default('function'),
    name: z.string(),
  })
  .catchall(z.any())
  .describe(
    'Message for passing the result of executing a tool back to a model.',
  );

// Tool Message
export const ToolMessage = z
  .object({
    ...MessageCommonFields,
    type: z.literal('tool').default('tool'),
    tool_call_id: z.string(),
    artifact: z.any().default(null),
    status: z.enum(['success', 'error']).default('success'),
  })
  .catchall(z.any())
  .describe(
    'Message for passing the result of executing a tool back to a model.',
  );

// AI Message Chunk
export const AIMessageChunk = z
  .object({
    ...MessageCommonFields,
    type: z.literal('AIMessageChunk').default('AIMessageChunk'),
    example: z.boolean().default(false),
    tool_calls: z.array(ToolCallSchema).default([]),
    invalid_tool_calls: z.array(InvalidToolCallSchema).default([]),
    usage_metadata: z.union([UsageMetadataSchema, z.null()]).default(null),
    tool_call_chunks: z.array(ToolCallChunkSchema).default([]),
  })
  .catchall(z.any())
  .describe('Message chunk from an AI.');

// Human Message Chunk
export const HumanMessageChunk = z
  .object({
    ...MessageCommonFields,
    type: z.literal('HumanMessageChunk').default('HumanMessageChunk'),
    example: z.boolean().default(false),
  })
  .catchall(z.any())
  .describe('Human Message chunk.');

// Chat Message Chunk
export const ChatMessageChunk = z
  .object({
    ...MessageCommonFields,
    type: z.literal('ChatMessageChunk').default('ChatMessageChunk'),
    role: z.string(),
  })
  .catchall(z.any())
  .describe('Chat Message chunk.');

// System Message Chunk
export const SystemMessageChunk = z
  .object({
    ...MessageCommonFields,
    type: z.literal('SystemMessageChunk').default('SystemMessageChunk'),
  })
  .catchall(z.any())
  .describe('System Message chunk.');

// Function Message Chunk
export const FunctionMessageChunk = z
  .object({
    ...MessageCommonFields,
    type: z.literal('FunctionMessageChunk').default('FunctionMessageChunk'),
    name: z.string(),
  })
  .catchall(z.any())
  .describe('Function Message chunk.');

// Tool Message Chunk
export const ToolMessageChunk = z
  .object({
    ...MessageCommonFields,
    type: z.literal('ToolMessageChunk').default('ToolMessageChunk'),
    tool_call_id: z.string(),
    artifact: z.any().default(null),
    status: z.enum(['success', 'error']).default('success'),
  })
  .catchall(z.any())
  .describe('Tool Message chunk.');

// Main Message type that combines all message types using discriminated union
export const Message = z.discriminatedUnion('type', [
  AIMessage,
  HumanMessage,
  ChatMessage,
  SystemMessage,
  FunctionMessage,
  ToolMessage,
  AIMessageChunk,
  HumanMessageChunk,
  ChatMessageChunk,
  SystemMessageChunk,
  FunctionMessageChunk,
  ToolMessageChunk,
]);

// Exported type
export type MessageType = z.infer<typeof Message>;

export const AgentChatEvent = z.object({
  type: z.enum(['messages', 'token']),
  message: Message,
});

export type TAgentChatEvent = z.infer<typeof AgentChatEvent>;

export type TAgentAIMessageChunk = z.infer<typeof AIMessageChunk>;

export type TAgentToolMessage = z.infer<typeof ToolMessage>;

const MaterializedCitationBlock = z.object({
  block_id: z.union([z.string(), z.string().uuid()]),
  level: z.enum(['document', 'page', 'section']),
  bounding_box: z.object({
    x1: z.number(),
    y1: z.number(),
    x2: z.number(),
    y2: z.number(),
  }),
  page_dimensions: z.object({ width: z.number(), height: z.number() }),
  page_number: z.number().int(),
  bates_number: z.string().nullish(),
});

export const MaterializedCitation = z.object({
  artifact_id: z.union([z.string(), z.string().uuid()]),
  name: z.string(),
  url: z.string(),
  mime_type: z.union([z.string(), z.null()]),
  blocks: z.array(MaterializedCitationBlock),
  bates_number: z.string().nullish(),
});

export type TMaterializedCitation = z.infer<typeof MaterializedCitation>;
export type TMaterializedCitationBlock = z.infer<
  typeof MaterializedCitationBlock
>;

export interface TAgentChatOrchestratorStatus {
  status: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'UNKNOWN' | 'ERROR';
}
